import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  Chip
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Schedule,
  CheckCircle,
  Cancel,
  Group,
  CalendarToday,
  Assessment
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';

import {
  schedulingService,
  MeetingStatus,
  SchedulingConfig
} from '../../services/schedulingService';

interface AnalyticsData {
  total_meetings: number;
  completed_meetings: number;
  cancelled_meetings: number;
  no_show_meetings: number;
  completion_rate: number;
  cancellation_rate: number;
  no_show_rate: number;
  daily_trends: Array<{
    date: string;
    meetings: number;
    completed: number;
    cancelled: number;
  }>;
  project_breakdown: Array<{
    project_name: string;
    project_code: string;
    meetings: number;
    completion_rate: number;
  }>;
  showroom_breakdown: Array<{
    showroom_location: string;
    meetings: number;
    completion_rate: number;
  }>;
  sales_team_performance: Array<{
    sales_contact_name: string;
    meetings: number;
    completion_rate: number;
  }>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const SchedulingAnalytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [configs, setConfigs] = useState<SchedulingConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date())
  });
  const [selectedProject, setSelectedProject] = useState<string>('');

  useEffect(() => {
    loadAnalyticsData();
    loadConfigurations();
  }, [dateRange, selectedProject]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get meetings data for the selected period
      const filters = {
        start_date: format(dateRange.start, 'yyyy-MM-dd'),
        end_date: format(dateRange.end, 'yyyy-MM-dd'),
        project_code: selectedProject || undefined
      };

      const meetings = await schedulingService.getScheduledMeetings(filters, 0, 1000);
      
      // Calculate analytics
      const totalMeetings = meetings.length;
      const completedMeetings = meetings.filter(m => m.status === MeetingStatus.COMPLETED).length;
      const cancelledMeetings = meetings.filter(m => m.status === MeetingStatus.CANCELLED).length;
      const noShowMeetings = meetings.filter(m => m.status === MeetingStatus.NO_SHOW).length;

      const completionRate = totalMeetings > 0 ? (completedMeetings / totalMeetings) * 100 : 0;
      const cancellationRate = totalMeetings > 0 ? (cancelledMeetings / totalMeetings) * 100 : 0;
      const noShowRate = totalMeetings > 0 ? (noShowMeetings / totalMeetings) * 100 : 0;

      // Daily trends
      const dailyTrends = generateDailyTrends(meetings, dateRange.start, dateRange.end);

      // Project breakdown
      const projectBreakdown = generateProjectBreakdown(meetings);

      // Showroom breakdown
      const showroomBreakdown = generateShowroomBreakdown(meetings);

      // Sales team performance
      const salesTeamPerformance = generateSalesTeamPerformance(meetings);

      setAnalyticsData({
        total_meetings: totalMeetings,
        completed_meetings: completedMeetings,
        cancelled_meetings: cancelledMeetings,
        no_show_meetings: noShowMeetings,
        completion_rate: completionRate,
        cancellation_rate: cancellationRate,
        no_show_rate: noShowRate,
        daily_trends: dailyTrends,
        project_breakdown: projectBreakdown,
        showroom_breakdown: showroomBreakdown,
        sales_team_performance: salesTeamPerformance
      });
    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError('Error al cargar los datos de analíticas');
    } finally {
      setLoading(false);
    }
  };

  const loadConfigurations = async () => {
    try {
      const data = await schedulingService.getSchedulingConfigs();
      setConfigs(data);
    } catch (err) {
      console.error('Error loading configurations:', err);
    }
  };

  const generateDailyTrends = (meetings: any[], startDate: Date, endDate: Date) => {
    const trends = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const dayMeetings = meetings.filter(m => m.meeting_date === dateStr);
      
      trends.push({
        date: format(currentDate, 'dd/MM'),
        meetings: dayMeetings.length,
        completed: dayMeetings.filter(m => m.status === MeetingStatus.COMPLETED).length,
        cancelled: dayMeetings.filter(m => m.status === MeetingStatus.CANCELLED).length
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return trends;
  };

  const generateProjectBreakdown = (meetings: any[]) => {
    const projectMap = new Map();

    meetings.forEach(meeting => {
      const key = meeting.project_code;
      if (!projectMap.has(key)) {
        projectMap.set(key, {
          project_name: meeting.project_name,
          project_code: meeting.project_code,
          meetings: 0,
          completed: 0
        });
      }

      const project = projectMap.get(key);
      project.meetings++;
      if (meeting.status === MeetingStatus.COMPLETED) {
        project.completed++;
      }
    });

    return Array.from(projectMap.values()).map(project => ({
      ...project,
      completion_rate: project.meetings > 0 ? (project.completed / project.meetings) * 100 : 0
    }));
  };

  const generateShowroomBreakdown = (meetings: any[]) => {
    const showroomMap = new Map();

    meetings.forEach(meeting => {
      const key = meeting.showroom_location;
      if (!showroomMap.has(key)) {
        showroomMap.set(key, {
          showroom_location: meeting.showroom_location,
          meetings: 0,
          completed: 0
        });
      }

      const showroom = showroomMap.get(key);
      showroom.meetings++;
      if (meeting.status === MeetingStatus.COMPLETED) {
        showroom.completed++;
      }
    });

    return Array.from(showroomMap.values()).map(showroom => ({
      ...showroom,
      completion_rate: showroom.meetings > 0 ? (showroom.completed / showroom.meetings) * 100 : 0
    }));
  };

  const generateSalesTeamPerformance = (meetings: any[]) => {
    const salesMap = new Map();

    meetings.forEach(meeting => {
      const key = meeting.sales_contact_name;
      if (!salesMap.has(key)) {
        salesMap.set(key, {
          sales_contact_name: meeting.sales_contact_name,
          meetings: 0,
          completed: 0
        });
      }

      const sales = salesMap.get(key);
      sales.meetings++;
      if (meeting.status === MeetingStatus.COMPLETED) {
        sales.completed++;
      }
    });

    return Array.from(salesMap.values()).map(sales => ({
      ...sales,
      completion_rate: sales.meetings > 0 ? (sales.completed / sales.meetings) * 100 : 0
    }));
  };

  const getStatusData = () => {
    if (!analyticsData) return [];

    return [
      { name: 'Completadas', value: analyticsData.completed_meetings, color: '#00C49F' },
      { name: 'Programadas', value: analyticsData.total_meetings - analyticsData.completed_meetings - analyticsData.cancelled_meetings - analyticsData.no_show_meetings, color: '#0088FE' },
      { name: 'Canceladas', value: analyticsData.cancelled_meetings, color: '#FF8042' },
      { name: 'No Asistió', value: analyticsData.no_show_meetings, color: '#FFBB28' }
    ];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Box>
        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filtros de Análisis
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Fecha Inicio"
                value={dateRange.start}
                onChange={(newValue) => {
                  if (newValue) {
                    setDateRange({ ...dateRange, start: newValue as Date });
                  }
                }}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Fecha Fin"
                value={dateRange.end}
                onChange={(newValue) => {
                  if (newValue) {
                    setDateRange({ ...dateRange, end: newValue as Date });
                  }
                }}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Proyecto</InputLabel>
                <Select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  label="Proyecto"
                >
                  <MenuItem value="">Todos los proyectos</MenuItem>
                  {configs.map((config) => (
                    <MenuItem key={config.project_code} value={config.project_code}>
                      {config.project_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {analyticsData && (
          <>
            {/* Key Metrics */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Total Citas
                        </Typography>
                        <Typography variant="h4" component="div">
                          {analyticsData.total_meetings}
                        </Typography>
                      </Box>
                      <Schedule color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Tasa de Completación
                        </Typography>
                        <Typography variant="h4" component="div" color="success.main">
                          {analyticsData.completion_rate.toFixed(1)}%
                        </Typography>
                      </Box>
                      <CheckCircle color="success" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          Tasa de Cancelación
                        </Typography>
                        <Typography variant="h4" component="div" color="error.main">
                          {analyticsData.cancellation_rate.toFixed(1)}%
                        </Typography>
                      </Box>
                      <Cancel color="error" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="body2">
                          No Asistieron
                        </Typography>
                        <Typography variant="h4" component="div" color="warning.main">
                          {analyticsData.no_show_rate.toFixed(1)}%
                        </Typography>
                      </Box>
                      <Group color="warning" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Daily Trends */}
              <Grid item xs={12} lg={8}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Tendencia Diaria de Citas
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analyticsData.daily_trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="meetings" stroke="#8884d8" name="Total" />
                      <Line type="monotone" dataKey="completed" stroke="#82ca9d" name="Completadas" />
                      <Line type="monotone" dataKey="cancelled" stroke="#ff7c7c" name="Canceladas" />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Status Distribution */}
              <Grid item xs={12} lg={4}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Distribución por Estado
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={getStatusData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {getStatusData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>

            {/* Project Performance */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} lg={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Rendimiento por Proyecto
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analyticsData.project_breakdown}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="project_code" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="meetings" fill="#8884d8" name="Total Citas" />
                      <Bar dataKey="completion_rate" fill="#82ca9d" name="% Completación" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Rendimiento por Showroom
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analyticsData.showroom_breakdown}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="showroom_location" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="meetings" fill="#8884d8" name="Total Citas" />
                      <Bar dataKey="completion_rate" fill="#82ca9d" name="% Completación" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>

            {/* Sales Team Performance */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Rendimiento del Equipo de Ventas
              </Typography>
              <Grid container spacing={2}>
                {analyticsData.sales_team_performance.map((sales, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {sales.sales_contact_name}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Citas: {sales.meetings}
                          </Typography>
                          <Typography 
                            variant="h6" 
                            color={sales.completion_rate >= 80 ? 'success.main' : 
                                   sales.completion_rate >= 60 ? 'warning.main' : 'error.main'}
                          >
                            {sales.completion_rate.toFixed(1)}%
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Advanced Scheduling Features */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Funciones Avanzadas
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Sistema de Notificaciones
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Gestiona notificaciones automáticas por WhatsApp, SMS y email para confirmaciones, recordatorios y cancelaciones.
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip label="Confirmaciones automáticas" size="small" />
                        <Chip label="Recordatorios 24h/2h" size="small" />
                        <Chip label="Multi-canal" size="small" />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Programación Inteligente
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Algoritmos de optimización para asignación de horarios, detección de conflictos y gestión de disponibilidad.
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip label="Detección de conflictos" size="small" />
                        <Chip label="Optimización automática" size="small" />
                        <Chip label="Gestión de capacidad" size="small" />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Analíticas Avanzadas
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Métricas detalladas de rendimiento, patrones de comportamiento y predicciones de demanda.
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip label="Predicción de demanda" size="small" />
                        <Chip label="Análisis de patrones" size="small" />
                        <Chip label="KPIs personalizados" size="small" />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Integración CRM
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Sincronización automática con sistemas CRM, scoring de leads y seguimiento de conversiones.
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip label="Lead scoring" size="small" />
                        <Chip label="Seguimiento automático" size="small" />
                        <Chip label="Sincronización CRM" size="small" />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default SchedulingAnalytics;